import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { FoundCard } from "../../Components/FoundCard/FoundCard";
import { ArticleCard } from "../../Components/ArticleCard/ArticleCard";
import CircularProgress from "@mui/material/CircularProgress";
import OrderCard from "../../Components/OrderCard/OrderCard";
import "./Search.css";
import TrustCard from "../../Components/TrustCard/TrustCard";
import { Card } from "../../Components/Card/Card";
import {
  search,
  fetchResults,
  scheduleSearch,
  simplifyResult,
} from "../../Utils/functions";
import { useNavigate } from "react-router-dom";
import { PicoContext } from "../../Context/PicoContext";
import { NavigationContext } from "../../Context/NavigationContext";
import { FilterContext } from "../../Context/FilterContext";
import { translateReport } from "../../Utils/functions";
import { Alert } from "@mui/material";
import UpgradeSubscriptionModal from "../../Components/UpgradeSubscriptionModal/UpgradeSubscriptionModal";
import { useAuthenticator } from "@aws-amplify/ui-react";
// import EmailNotificationCard from '../../Components/EmailNotificationCard/EmailNotification';

function Search() {
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pico, setPico } = useContext(PicoContext);
  const { filters, setFilters } = useContext(FilterContext);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [isModalClosed] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  // const [showEmailNotif, setShowEmailNotif] = useState(false);
  const [screenedArticles, setScreenedArticles] = useState([]);
  const [originalArticles, setOriginalArticles] = useState([]);
  const [reportError, setReportError] = useState("");
  const [synthesisResult, setSynthesisResult] = useState(null);
  const [simplifiedResult, setSimplifiedResult] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [qualifiedAbstracts, setQualifiedAbstracts] = useState(0);
  const [totalAbstracts, setTotalAbstracts] = useState(0);
  const [qualifiedOrgDocuments, setQualifiedOrgDocuments] = useState(0);
  const [totalOrgDocuments, setTotalOrgDocuments] = useState(0);
  const [question, setQuestion] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [questionUpdateRequired, setQuestionUpdateRequired] = useState(false);

  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isLoadingSimplification, setILoadingSimplication] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("original");
  const { setRefreshTrigger } = useContext(NavigationContext);

  const handleLanguageChange = async (language) => {
    if (language === selectedLanguage) {
      return;
    }

    try {
      setIsLoadingReport(true);
      setSelectedLanguage(language);

      const result = await translateReport(language, id);
      if (result) {
        if (result.originalTranslatedSummary)
          setSynthesisResult(result.originalTranslatedSummary);
        if (
          result.simplifiedTranslatedSummary &&
          result.simplifiedTranslatedSummary.trim() !== ""
        )
          setSimplifiedResult(result.simplifiedTranslatedSummary);
        if (result.translatedArticles && selectedReportType === "original") {
          const sortedTranslatedArticles = result.translatedArticles.sort(
            (a, b) => a.gradeData.GradeNumber - b.gradeData.GradeNumber
          );
          setScreenedArticles(sortedTranslatedArticles);
          setOriginalArticles(sortedTranslatedArticles);
        }
        if (
          result.translatedSimplifiedArticles &&
          selectedReportType === "plainLanguage"
        ) {
          const sortedSimplifiedArticles = result.translatedSimplifiedArticles.sort(
            (a, b) => a.gradeData.GradeNumber - b.gradeData.GradeNumber
          );
          setScreenedArticles(sortedSimplifiedArticles);
        } else {
          console.warn("No translated articles returned.");
        }
      }
    } catch (error) {
      console.error("Error translating synthesis:", error);
      setErrorMessage("Failed to translate synthesis. Please try again.");
    } finally {
      setIsLoadingReport(false);
    }
  };

  const handleReportTypeChange = async (reportType) => {
    setSelectedReportType(reportType);
    if (reportType === "plainLanguage") {
      try {
        setILoadingSimplication(true);
        const simplifiedData = await simplifyResult(id, selectedLanguage);
        if (
          simplifiedData &&
          simplifiedData.simplifiedSummary &&
          simplifiedData.simplifiedArticles
        ) {
          setSimplifiedResult(simplifiedData.simplifiedSummary);
          setScreenedArticles(simplifiedData.simplifiedArticles);
        }
      } catch (error) {
        console.error("Error simplifying result ", error);
        setErrorMessage("Failed to simplify result. Please try again later.");
      } finally {
        setILoadingSimplication(false);
      }
    } else if (reportType === "original") {
      setScreenedArticles((prev) => {
        return originalArticles.length > 0 ? originalArticles : prev;
      });
    }
  };

  const normaliseDate = (dateValue, yearValue, fallbackDay) => {
    if (dateValue) {
      return dateValue;
    }
    if (yearValue) {
      return `${yearValue}/${fallbackDay}`;
    }
    return "";
  };

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        setIsLoading(true);
        const data = await fetchResults(id);
        setStatus(data.status);
        // If the job is complete, stop polling and display the result
        if (data.status === "Completed") {
          const sortedArticles = data.translatedArticles.sort(
            (a, b) => a.gradeData.GradeNumber - b.gradeData.GradeNumber
          );
          setScreenedArticles(sortedArticles);
          setOriginalArticles(sortedArticles);
          setSynthesisResult(data.translatedSynthesisResult);
          if (data.translatedSimplifiedSummary) {
            setSimplifiedResult(data.translatedSimplifiedSummary);
          }
          setQualifiedAbstracts(data.qualifiedAbstracts);
          setSelectedReportType("original");
          setTotalAbstracts(data.totalAbstracts);
          setQuestion(data.question);
          setEmail(data.email);
          setDate(data.updatedAt);
          setSelectedLanguage(data.languagePreferred);
          setQualifiedOrgDocuments(
            data.extractedData.filter((x) => x.isPrivate === true).length
          );
          setTotalOrgDocuments(
            data.extractedData.filter((x) => x.isPrivate === true).length
          );

          setFilters({
            fromDate: normaliseDate(data.fromDate, data.fromYear, "01/01"),
            toDate: normaliseDate(data.toDate, data.toYear, "12/31"),
            abstracts: data.userSelectedAbstracts,
            studyDesigns: data.studyDesigns || [],
          });
          setQuery(data.query);
          setPico({
            P: data.pico.P || "",
            I: data.pico.I || "",
            C: data.pico.C || "",
            O: data.pico.O || "",
            IC: data.pico.IC || "",
            EC: data.pico.EC || "",
          });
          setRefreshTrigger((prev) => !prev);
          clearInterval(intervalId);
          setIsLoading(false);
        } else if (
          data.status === "Error" &&
          data.error ===
            "No sufficient articles found. Please try with different search."
        ) {
          const data = await fetchResults(id);
          setQuestion(data.question);
          setQuery(data.query);
          setPico({
            P: data.pico.P || "",
            I: data.pico.I || "",
            C: data.pico.C || "",
            O: data.pico.O || "",
            IC: data.pico.IC || "",
            EC: data.pico.EC || "",
          });
          setFilters({
            fromDate: normaliseDate(data.fromDate, data.fromYear, "01/01"),
            toDate: normaliseDate(data.toDate, data.toYear, "12/31"),
            abstracts: data.userSelectedAbstracts,
            studyDesigns: data.studyDesigns || [],
          });
          setQualifiedAbstracts(data.qualifiedAbstracts);
          setTotalAbstracts(data.totalAbstracts);
          setQuestionUpdateRequired(true);
          setIsLoading(false);
          clearInterval(intervalId);
        } else if (data.status === "Error") {
          setQuestion(data.question || "");
          setQuery(data.query || "");
          if (data.pico) {
            setPico({
              P: data.pico.P || "",
              I: data.pico.I || "",
              C: data.pico.C || "",
              O: data.pico.O || "",
              IC: data.pico.IC || "",
              EC: data.pico.EC || "",
            });
          }
          setFilters({
            fromDate: normaliseDate(data.fromDate, data.fromYear, "01/01"),
            toDate: normaliseDate(data.toDate, data.toYear, "12/31"),
            abstracts: data.userSelectedAbstracts,
            studyDesigns: data.studyDesigns || [],
          });
          setQualifiedAbstracts(data.qualifiedAbstracts || 0);
          setTotalAbstracts(data.totalAbstracts || 0);
          const articles = Array.isArray(data.translatedArticles)
            ? data.translatedArticles
            : Array.isArray(data.extractedData)
              ? data.extractedData
              : [];
          if (articles.length > 0) {
            const sortedArticles = [...articles].sort(
              (a, b) =>
                (a.gradeData?.GradeNumber || 0) -
                (b.gradeData?.GradeNumber || 0)
            );
            setScreenedArticles(sortedArticles);
            setOriginalArticles(sortedArticles);
          }
          setReportError(
            data.error ||
              "A relevant summary based on available research could not be created."
          );
          setIsLoading(false);
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error fetching job status:", error);
        clearInterval(intervalId);
        setIsLoading(false);
      }
    }, 1000); // Poll every 3 seconds

    return () => clearInterval(intervalId);
  }, [id, isModalClosed, setRefreshTrigger]);

  const handleSearchAndNavigate = async (question) => {
    try {
      const result = await search(question, filters, pico, query); // Assuming search is an async function

      if (result.error) {
        setErrorMessage(result.error);
        return;
      }

      if (result.id) {
        navigate(`/search/${result.id}`, { replace: true }); // Replace with your actual path and ensure navigation is replaced to avoid duplicate history entries
      }
    } catch (error) {
      console.error("Error during search:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  const handleScheduleSearch = async (question, searchMode, frequency) => {
    try {
      await scheduleSearch(
        question,
        filters,
        pico,
        query,
        searchMode,
        frequency
      );
    } catch (error) {
      console.error("Error during search:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  const closeErrorModal = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    if (email) {
      setEmailMessage(`Report sent to ${email} on ${date}`);
    }
  }, [email, date]);
  const totalAbstractsValue = Number.isFinite(totalAbstracts)
    ? totalAbstracts
    : 0;
  const qualifiedAbstractsValue = Number.isFinite(qualifiedAbstracts)
    ? qualifiedAbstracts
    : 0;
  const getGuidanceMessages = () => {
    const guidanceMessages = [];
    if (questionUpdateRequired) {
      guidanceMessages.push(
        "We couldn't find enough results to complete your report."
      );
    }
    if (!isLoading && totalAbstractsValue < 10) {
      guidanceMessages.push(
        "Fewer than 10 abstracts were found. Please update your question."
      );
    }
    if (!isLoading && qualifiedAbstractsValue < 1) {
      guidanceMessages.push(
        "No abstracts met your qualifying criteria. Please update your PICO selections and filters."
      );
    }
    return guidanceMessages;
  };

  return (
    <>
      {isLoading ? (
        <div className="loading-container">
          <CircularProgress />
          <p className="loading-status">{status}</p>
        </div>
      ) : (
        <div className="SearchResults">
          {reportError && (
            <Alert severity="error" className="search-error-alert">
              {reportError} You can try the search again below
              {screenedArticles.length > 0
                ? ", or review the articles that were found."
                : "."}
            </Alert>
          )}
          {questionUpdateRequired && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {getGuidanceMessages().map((message, index) => (
                <div key={index}>{message}</div>
              ))}
            </Alert>
          )}
          <Card
            defaultText={question}
            search={handleSearchAndNavigate}
            scheduleSearch={handleScheduleSearch}
            setQuery={setQuery}
            query={query}
          />
          <h3> {emailMessage} </h3>
          {synthesisResult && (
            <>
              <OrderCard>
                <TrustCard
                  trustText={
                    selectedReportType === "original"
                      ? synthesisResult
                      : simplifiedResult
                  }
                  isLoadingReport={isLoadingReport}
                  isLoadingSimplification={isLoadingSimplification}
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                  selectedReportType={selectedReportType}
                  onReportTypeChange={handleReportTypeChange}
                />
              </OrderCard>
              <div>
                <p>
                  Please take a moment to provide your feedback:{" "}
                  <a href="https://www.surveymonkey.com/r/MJGBFFY">
                    MedRPT Feedback Survey
                  </a>
                </p>
              </div>
            </>
          )}
          <FoundCard
            qualifiedAbstracts={qualifiedAbstracts}
            totalAbstracts={totalAbstracts}
            qualifiedOrgDocuments={qualifiedOrgDocuments}
            totalOrgDocuments={totalOrgDocuments}
          />
          <OrderCard>
            {screenedArticles.map((article, index) => (
              <div
                key={index}
                id={`paper-${index + 1}`}
                className="article-container"
              >
                <ArticleCard
                  key={index}
                  title={article.title}
                  date={article.publicationDate}
                  authors={article.authors}
                  journal={article.journalTitle}
                  insight={article.insight}
                  results={article.extracted}
                  citations={article.citationCount}
                  uniqueId={article.uniqueId || article.pubmed_id}
                  index={index}
                  gradeData={article.gradeData}
                  isPrivate={article.isPrivate}
                  language={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                />
              </div>
            ))}
          </OrderCard>
        </div>
      )}
      {/*
      {showEmailNotif && (
        <EmailNotificationCard
        type="success"
        onClose={() => setShowEmailNotif(false)}
        />
      )}
      */}
      {errorMessage && (
        <UpgradeSubscriptionModal
          message={errorMessage}
          onClose={closeErrorModal}
        />
      )}
    </>
  );
}

export default Search;
